/* Classic Shop — newsletter popup (sections/cs-newsletter-popup.liquid)
 *
 * sessionStorage key "cs-nlp-state":
 *   (absent)      → not shown yet in this session: open after the configured delay
 *   "shown"       → opened once; never auto-opened again in this session
 *   "closed"      → closed by X / overlay / Escape
 *   "subscribed"  → successful subscription
 *   "suppressed"  → popup disabled for this browser session (selected paid campaigns)
 *   "pending"     → form submitted (native Shopify customer form);
 *                   the next page load reopens the popup with the result
 */
(() => {
  const KEY = 'cs-nlp-state';

  // Meta campaigns for which the fullscreen newsletter popup
  // must stay disabled for the entire browser session.
  const SUPPRESSED_CAMPAIGNS = [
    'brand_film_oct',
    'video_landing_test_oct',
  ];

  const storage = {
    get() {
      try {
        return window.sessionStorage.getItem(KEY);
      } catch (e) {
        return null;
      }
    },

    set(value) {
      try {
        window.sessionStorage.setItem(KEY, value);
      } catch (e) {
        /* storage blocked: show once per page */
      }
    },
  };

  function init(dialog) {
    if (!dialog || dialog.dataset.csNlpReady) return;

    dialog.dataset.csNlpReady = 'true';

    const designMode = dialog.dataset.designMode === 'true';
    const form = dialog.querySelector('form');
    const submit = dialog.querySelector('.cs-nlp__submit');
    const status = dialog.querySelector('[data-cs-nlp-status]');
    const success = dialog.querySelector('[data-cs-nlp-success]');
    const img = dialog.querySelector('.cs-nlp__img');
    const input = dialog.querySelector('.cs-nlp__input');
    const field = dialog.querySelector('.cs-nlp__field');

    const errorText =
      status?.dataset.errorText ||
      'Не вдалося оформити підписку. Спробуйте ще раз.';

    const invalidText =
      status?.dataset.invalidText ||
      'Введіть коректний email.';

    // Stricter than type=email alone (which accepts "a@b"):
    // needs a dot in the domain.
    const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

    let submitting = false;

    // The validation message is shown in the popup's own status line instead
    // of the browser bubble (which would cover the artwork).
    // Without JS the native check still applies.
    if (form) form.noValidate = true;

    const setInvalid = (invalid) => {
      field?.classList.toggle('is-invalid', invalid);

      if (invalid) {
        input?.setAttribute('aria-invalid', 'true');
      } else {
        input?.removeAttribute('aria-invalid');
      }
    };

    input?.addEventListener('input', () => {
      if (!field?.classList.contains('is-invalid')) return;

      setInvalid(false);

      if (status) {
        status.textContent = '';
      }
    });

    // Fetch the artwork once the page has finished loading,
    // so the popup never opens empty and never competes
    // with the homepage's own images.
    const preload = () => {
      if (img) {
        img.loading = 'eager';
      }
    };

    const open = () => {
      if (dialog.open) return;

      if (img) {
        img.loading = 'eager';
      }

      document.documentElement.classList.add('cs-nlp-lock');
      dialog.showModal();
    };

    const close = () => {
      if (dialog.open) {
        dialog.close();
      }
    };

    const showSuccess = () => {
      if (!designMode) {
        storage.set('subscribed');
      }

      dialog.classList.add('is-success');

      if (status) {
        status.textContent = '';
      }

      if (success) {
        success.hidden = false;
        success.setAttribute('tabindex', '-1');
        success.focus({ preventScroll: true });
      }
    };

    const showError = (text) => {
      if (status) {
        status.textContent = text || errorText;
      }
    };

    // Closing (X button, Escape via native "cancel", overlay click)
    // always ends up here.
    dialog.addEventListener('close', () => {
      if (submitting) return;

      document.documentElement.classList.remove('cs-nlp-lock');

      if (!designMode && storage.get() !== 'subscribed') {
        storage.set('closed');
      }
    });

    dialog
      .querySelector('[data-cs-nlp-close]')
      ?.addEventListener('click', close);

    // Click on the backdrop: the event target is the <dialog> itself.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        close();
      }
    });

    // Escape also closes the non-modal (submitting) state,
    // where the native "cancel" does not fire.
    document.addEventListener('keydown', (event) => {
      if (
        event.key === 'Escape' &&
        dialog.open &&
        dialog.classList.contains('is-submitting') &&
        !submitting
      ) {
        close();
      }
    });

    // Regular (native) Shopify customer-form submission —
    // identical to the footer email-signup form,
    // so Shopify bot protection (hCaptcha) keeps working.
    //
    // Shopify redirects back with ?customer_posted=true
    // and the next page load reopens the popup with the result.
    form?.addEventListener('submit', (event) => {
      if (submitting) {
        event.preventDefault();
        return;
      }

      if (input) {
        input.value = input.value.trim();
      }

      if (
        !input ||
        !input.checkValidity() ||
        !EMAIL_RE.test(input.value)
      ) {
        event.preventDefault();

        setInvalid(true);

        if (status) {
          status.textContent = invalidText;
        }

        input?.focus();
        return;
      }

      setInvalid(false);
      submitting = true;

      if (!designMode) {
        storage.set('pending');
      }

      submit?.setAttribute('aria-busy', 'true');

      if (status) {
        status.textContent = '';
      }

      // Leave modal mode so a possible captcha challenge
      // is not covered/inert.
      if (dialog.open) {
        dialog.close();
        dialog.classList.add('is-submitting');
        dialog.show();
      }

      // If no navigation starts (e.g. the captcha was dismissed),
      // make the form usable again.
      let leaving = false;

      window.addEventListener(
        'beforeunload',
        () => {
          leaving = true;

          if (!designMode) {
            storage.set('pending');
          }
        },
        { once: true }
      );

      window.setTimeout(() => {
        if (leaving) return;

        submitting = false;
        submit?.removeAttribute('aria-busy');

        if (!designMode) {
          storage.set('shown');
        }
      }, 4000);
    });

    if (designMode) return;

    /*
     * PAID TRAFFIC POPUP EXCLUSION
     *
     * Visitors coming from selected Meta campaigns should not
     * see the newsletter popup during this browser session.
     *
     * Supported campaigns:
     * - brand_film_oct
     * - video_landing_test_oct
     *
     * Example:
     * ?utm_campaign=video_landing_test_oct
     *
     * The state is stored in sessionStorage, so suppression
     * remains active when the visitor navigates:
     *
     * homepage → collection → product → cart
     */
    const params = new URLSearchParams(window.location.search);
    const campaign = params.get('utm_campaign');

    if (
      SUPPRESSED_CAMPAIGNS.includes(campaign) &&
      !storage.get()
    ) {
      storage.set('suppressed');
    }

    const state = storage.get();

    // Shopify bot-protection page (/challenge):
    // never auto-open over the captcha,
    // and keep "pending" untouched so the result
    // is shown after the redirect back.
    if (window.location.pathname.startsWith('/challenge')) {
      return;
    }

    // Returning from a native (fallback) submission.
    if (state === 'pending') {
      const posted = status?.dataset.csNlpStatus;

      if (
        posted === 'success' ||
        window.location.search.includes('customer_posted=true')
      ) {
        open();
        showSuccess();
      } else if (posted === 'error') {
        storage.set('shown');
        open();
        showError();
      } else {
        storage.set('closed');
      }

      return;
    }

    // "shown", "closed", "subscribed" or "suppressed":
    // never auto-open again during this session.
    if (state) return;

    if (document.readyState === 'complete') {
      preload();
    } else {
      window.addEventListener('load', preload, { once: true });
    }

    const delay =
      Math.max(1, Number(dialog.dataset.delay) || 4) * 1000;

    window.setTimeout(() => {
      if (storage.get()) return;

      storage.set('shown');
      open();
    }, delay);
  }

  const initAll = () => {
    document
      .querySelectorAll('[data-cs-nlp]')
      .forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor:
  // never auto-open; open while the section is selected.
  document.addEventListener('shopify:section:load', initAll);

  document.addEventListener('shopify:section:select', (event) => {
    const dialog = document.querySelector(
      `[data-cs-nlp][data-section-id="${event.detail.sectionId}"]`
    );

    if (dialog && !dialog.open) {
      document.documentElement.classList.add('cs-nlp-lock');
      dialog.showModal();
    }
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const dialog = document.querySelector(
      `[data-cs-nlp][data-section-id="${event.detail.sectionId}"]`
    );

    if (dialog?.open) {
      dialog.close();
    }
  });
})();